/**
 * AuthedImage — renders an <img> for resources that require Authorization header.
 *
 * Browsers cannot attach Bearer tokens to <img src=...> requests. This wrapper
 * fetches the resource with the user's token, turns the response into a blob URL,
 * and sets that on a regular <img>. Blob URL is revoked on unmount/url change so
 * memory doesn't leak across re-renders.
 */
import { useEffect, useState } from "react";
import { auth } from "../api.js";
import { apiUrl } from "../lib/apiUrl.js";

interface AuthedImageProps {
  url: string | null | undefined;
  alt: string;
  className?: string;
  fallback?: React.ReactNode;
}

export function AuthedImage({ url, alt, className, fallback }: AuthedImageProps) {
  const [blob, setBlob] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setBlob(null);
      return;
    }
    let cancelled = false;
    let createdUrl: string | null = null;
    setErr(null);

    (async () => {
      try {
        const token = auth.getToken();
        const res = await fetch(apiUrl(url), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          if (!cancelled) setErr(`HTTP ${res.status}`);
          return;
        }
        const b = await res.blob();
        createdUrl = URL.createObjectURL(b);
        if (cancelled) {
          URL.revokeObjectURL(createdUrl);
        } else {
          setBlob(createdUrl);
        }
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [url]);

  if (!url) return <>{fallback ?? null}</>;
  // Fall back to placeholder on fetch error (e.g., 404 for assets not yet
  // generated). Only show raw error text if no fallback is provided.
  if (err) return <>{fallback ?? <span className="text-xs text-tabrakan">⚠ {err}</span>}</>;
  if (!blob) return <span className="text-xs text-slate-400">memuat…</span>;
  return <img src={blob} alt={alt} className={className} />;
}
